import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiHome, FiList, FiSettings, FiUsers, FiCheckSquare, FiPieChart, FiDollarSign, FiTrendingDown, FiActivity, FiChevronLeft, FiChevronRight, FiChevronDown, FiFileText, FiArchive, FiCalendar, FiSearch } from 'react-icons/fi';
import { useUI } from '../context/UIContext';

/**
 * Navigation ist in Themengruppen gegliedert. Jede Gruppe hat eine
 * ausklappbare Überschrift; der Zustand (ein-/ausgeklappt) liegt im
 * localStorage, damit er über Reloads und Sitzungen erhalten bleibt.
 *
 * Die Gruppe der aktiven Seite ist immer aufgeklappt und farblich
 * hervorgehoben – so ist auf einen Blick klar, wo man sich befindet.
 */
const NAV_GROUPS = [
  {
    id: 'start',
    label: 'Start',
    items: [
      { path: '/', icon: FiHome, label: 'Übersicht' },
    ],
  },
  {
    // Kunden, Umsätze und die Abrechnung gehören fachlich zusammen: die
    // Provision ist der Anspruch des Kunden aus seinem Umsatz, und die
    // Auszahlung an ihn wird ebenfalls hier getätigt.
    id: 'customers',
    label: 'Kunden & Abrechnung',
    items: [
      { path: '/customers', icon: FiUsers, label: 'Kunden' },
      { path: '/revenues', icon: FiDollarSign, label: 'Umsätze' },
      { path: '/segmentation', icon: FiPieChart, label: 'Segmente' },
      { path: '/commission-statements', icon: FiFileText, label: 'Provisionsabrechnungen' },
      { path: '/payout-reconciliation', icon: FiDollarSign, label: 'Auszahlungs-Abgleich' },
    ],
  },
  {
    id: 'expenses',
    label: 'Ausgaben & Belege',
    items: [
      { path: '/expenses', icon: FiList, label: 'Transaktionen' },
      { path: '/analysis', icon: FiTrendingDown, label: 'Ausgaben Analyse' },
      { path: '/receipts', icon: FiArchive, label: 'Belegarchiv' },
      { path: '/receipt-ocr', icon: FiSearch, label: 'OCR-Prüfung' },
    ],
  },
  {
    id: 'tax',
    label: 'Steuer',
    items: [
      { path: '/tax-report', icon: FiFileText, label: 'Steuerbericht' },
    ],
  },
  {
    id: 'organisation',
    label: 'Organisation',
    items: [
      { path: '/tasks', icon: FiCheckSquare, label: 'Aufgaben' },
      { path: '/task-calendar', icon: FiCalendar, label: 'Aufgaben-Kalender' },
      { path: '/activity', icon: FiActivity, label: 'Aktivitäten' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { path: '/settings', icon: FiSettings, label: 'Einstellungen' },
    ],
  },
];

/** Bottom-Navigation (mobil) – die sechs wichtigsten Einstiegspunkte. */
const MOBILE_PATHS = ['/', '/customers', '/expenses', '/revenues', '/tax-report', '/tasks'];

const GROUPS_STORAGE_KEY = 'chargenest_nav_groups';

/** Pfad-Matching wie NavLink: '/' gilt nur exakt. */
const isPathActive = (path, pathname) =>
  path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);

const Navigation = () => {
  // Sidebar collapsed-State (zentral via UIContext)
  const { sidebarCollapsed: isCollapsed, toggleSidebar } = useUI();
  const location = useLocation();
  const activeRef = useRef(null);

  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(collapsedGroups));
    } catch (e) { /* localStorage nicht verfügbar – Groups bleiben offen */ }
  }, [collapsedGroups]);

  const activeGroupId = useMemo(() => {
    const group = NAV_GROUPS.find((g) => g.items.some((item) => isPathActive(item.path, location.pathname)));
    return group ? group.id : null;
  }, [location.pathname]);

  // Die Gruppe der aktiven Seite bleibt immer aufgeklappt.
  useEffect(() => {
    if (!activeGroupId) return;
    setCollapsedGroups((prev) => (prev[activeGroupId] ? { ...prev, [activeGroupId]: false } : prev));
  }, [activeGroupId]);

  // Aktiven Eintrag in den sichtbaren Bereich scrollen.
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [location.pathname, isCollapsed]);

  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const mobileItems = useMemo(
    () => MOBILE_PATHS
      .map((path) => NAV_GROUPS.flatMap((g) => g.items).find((item) => item.path === path))
      .filter(Boolean),
    []
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <nav
        className={`hidden md:flex flex-col ${
          isCollapsed ? 'w-16' : 'w-64'
        } bg-white dark:bg-gray-900 h-screen fixed left-0 top-0 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-40`}
      >
        {/* Header mit mittigem Logo und Toggle */}
        <div className="relative p-4 flex items-center border-b border-gray-100 dark:border-gray-700 h-20">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {!isCollapsed ? (
              <div className="w-32 h-16 flex items-center justify-center">
                <img
                  src="https://media-manager-c.questera.ai/greta-media/b64cc446cdd47675bdad19de596fda49c979fcc46f742bafa38ab384e40234c3cc3101a26c56d32c5dccb06920b38db8/images/aW1hZ2UvcG5n/032e1220d38820869e7fe9bbbe32aea8.png"
                  alt="ChargeNest Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src="https://media-manager-c.questera.ai/greta-media/b64cc446cdd47675bdad19de596fda49c979fcc46f742bafa38ab384e40234c3cc3101a26c56d32c5dccb06920b38db8/images/aW1hZ2UvcG5n/29600e09bdecda10c6f2db2f15e79492.png"
                  alt="ChargeNest Symbol"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
          <button
            onClick={() => toggleSidebar()}
            className="absolute z-20 -right-4 top-1/2 -translate-y-1/2 p-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
            title={isCollapsed ? 'Sidebar ausklappen' : 'Sidebar einklappen'}
          >
            <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="text-lg" />
          </button>
        </div>

        <div className="flex-1 px-2 py-3 overflow-y-auto overflow-x-hidden">
          {NAV_GROUPS.map((group, groupIndex) => {
            const isGroupActive = group.id === activeGroupId;
            // Im eingeklappten Zustand gibt es keine Gruppen, nur Icons.
            const isOpen = isCollapsed ? true : (!collapsedGroups[group.id] || isGroupActive);

            return (
              <div key={group.id}>
                {!isCollapsed && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={isOpen}
                    className={`w-full flex items-center justify-between gap-2 px-3 ${groupIndex === 0 ? 'pt-1' : 'pt-4'} pb-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                      isGroupActive
                        ? 'text-indigo-500 dark:text-indigo-400'
                        : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
                    }`}
                    title={`${group.label} ${isOpen ? 'einklappen' : 'ausklappen'}`}
                  >
                    <span className="truncate">{group.label}</span>
                    <SafeIcon icon={isOpen ? FiChevronDown : FiChevronRight} className="text-sm shrink-0" />
                  </button>
                )}

                {isOpen && group.items.map((item) => {
                  const itemActive = isPathActive(item.path, location.pathname);
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      ref={itemActive ? activeRef : null}
                      title={isCollapsed ? item.label : undefined}
                      className={({ isActive }) => `
                        flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl transition-all duration-200 mb-0.5
                        ${isActive
                          ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}
                      `}>
                      <SafeIcon icon={item.icon} className="text-xl shrink-0" />
                      {!isCollapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>

        {!isCollapsed && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-center text-gray-400">&copy; {new Date().getFullYear()} ChargeNest</p>
          </div>
        )}
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 pb-safe z-50">
        <div className="flex justify-around items-center h-16 px-2 overflow-x-auto">
          {mobileItems.map((item) => (
            <NavLink key={item.path} to={item.path}
              className={({ isActive }) => `
                flex flex-col items-center justify-center w-full h-full space-y-1 min-w-[60px]
                ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}
              `}>
              <SafeIcon icon={item.icon} className="text-2xl" />
              <span className="text-[10px] font-medium truncate w-full text-center px-1">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
};

export default Navigation;
